import React, { useCallback } from 'react';

import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Divider from '@material-ui/core/Divider';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import SendIcon from '@material-ui/icons/Send';

import { pluralize } from '../../lib/helpers/text.helpers';
import { PrimaryButton } from '../_core/_ui/buttons.component';
import { IntlMsg } from '../_core/IntlMsg/intl-msg.component';
import { ReportCardAvailabilityMessages } from './report-card-access.types';
import { useReportCardAvailabilityStyles } from './report-card-availability.styles';
import { useSendReportCards } from './send-report-cards-dialog.use-send';
import { SendReportCardsPreview } from './send-report-cards-preview.component';
import { SendReportCardsSetting } from './send-report-cards-setting.component';

interface ISendReportCardsDialogProps {
  open: boolean;
  ageGroup: IAgeGroup;
  availability: IReportCardAvailability;
  messages: ReportCardAvailabilityMessages;
  nouns: { one: string; many: string };
  onClose: () => void;
  // Re-enables the age group's access; offered instead of sending while it is turned off.
  onReEnable: () => void;
  reEnabling: boolean;
}

export const SendReportCardsDialog: React.FC<ISendReportCardsDialogProps> = ({
  open,
  ageGroup,
  availability,
  messages,
  nouns,
  onClose,
  onReEnable,
  reEnabling,
}) => {
  const classes = useReportCardAvailabilityStyles();
  const send = useSendReportCards({ ageGroup, availability, open, messages, onSent: onClose });
  const { preview, outcome, saveSetting, send: runSend } = send;
  const handleSettingChange = useCallback((value: boolean | null) => { saveSetting(value); }, [saveSetting]);
  const handleSend = useCallback(() => { runSend(); }, [runSend]);
  const busy = send.busy || reEnabling;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth aria-labelledby="send-report-cards-title">
      <DialogTitle id="send-report-cards-title">
        <IntlMsg {...messages.titleSend} values={{ ageGroup: ageGroup.name }} />
      </DialogTitle>
      <DialogContent>
        <SendReportCardsSetting
          availability={availability}
          messages={messages}
          disabled={busy || outcome === 'REVOKED'}
          saving={send.settingInFlight}
          onChange={handleSettingChange}
        />
        <Divider className={classes.section} />
        <div className={classes.section}>
          <SendReportCardsPreview
            ageGroupName={ageGroup.name}
            preview={preview}
            outcome={outcome}
            error={send.previewError}
            messages={messages}
            nouns={nouns}
            onRetry={send.retryPreview}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}><IntlMsg {...messages.btnCancel} /></Button>
        {outcome === 'REVOKED' ? (
          <PrimaryButton
            startIcon={reEnabling ? <CircularProgress size={16} color="inherit" /> : <LockOpenIcon />}
            disabled={busy}
            onClick={onReEnable}
          >
            <IntlMsg {...messages.btnReEnableAgeGroup} />
          </PrimaryButton>
        ) : (
          <PrimaryButton
            endIcon={send.sendInFlight ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            disabled={!send.canSend}
            onClick={handleSend}
          >
            {preview && preview.playerCount > 0 ? (
              <IntlMsg
                {...messages.btnConfirmSend}
                values={{
                  playerCount: preview.playerCount,
                  players: pluralize(preview.playerCount, nouns.one, nouns.many),
                  contactCount: preview.notifiedContactCount,
                  contacts: pluralize(preview.notifiedContactCount, 'contact'),
                }}
              />
            ) : <IntlMsg {...(outcome ? messages.btnNothingToSend : messages.btnSendPending)} />}
          </PrimaryButton>
        )}
      </DialogActions>
    </Dialog>
  );
};
